import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';

import { useFirmante, useUpdateFirmante } from '../hooks/useConfiguracion';

const firmanteSchema = z.object({
  titulo: z.string().min(1, 'Requerido').max(100, 'Máximo 100 caracteres'),
  nombre: z.string().min(1, 'Requerido').max(100, 'Máximo 100 caracteres'),
  matricula: z.string().min(1, 'Requerido').max(50, 'Máximo 50 caracteres'),
});

type FirmanteFormValues = z.infer<typeof firmanteSchema>;

const DEFAULT_VALUES: FirmanteFormValues = { titulo: '', nombre: '', matricula: '' };

export function FirmanteSection(): JSX.Element {
  const notify = useNotify();
  const query = useFirmante();
  const mutation = useUpdateFirmante();
  const [saveError, setSaveError] = useState<unknown>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, errors },
  } = useForm<FirmanteFormValues>({
    resolver: zodResolver(firmanteSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (query.data) {
      reset({ titulo: query.data.titulo, nombre: query.data.nombre, matricula: query.data.matricula });
    }
  }, [query.data, reset]);

  const isNotFound = isApiError(query.error) && query.error.isNotFound();
  const hasLoadError = query.isError && !isNotFound;
  const hasFormErrors = Object.keys(errors).length > 0;

  async function onSubmit(values: FirmanteFormValues): Promise<void> {
    setSaveError(null);
    try {
      const updated = await mutation.mutateAsync(values);
      notify.success('Firmante actualizado.');
      reset({ titulo: updated.titulo, nombre: updated.nombre, matricula: updated.matricula });
    } catch (err) {
      if (isApiError(err) && err.isValidationError()) {
        err.validationErrors().forEach(({ field, message }) => {
          setError(field as keyof FirmanteFormValues, { type: 'server', message });
        });
      } else {
        setSaveError(err);
      }
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Firmante de informes
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Datos del profesional que firma los informes generados.
      </Typography>

      {query.isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </Box>
      ) : hasLoadError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          }
        >
          {getErrorMessage(query.error)}
        </Alert>
      ) : (
        <>
          {saveError != null && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {getErrorMessage(saveError)}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <Controller
              control={control}
              name="titulo"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Título *"
                  size="small"
                  fullWidth
                  placeholder="Médico Veterinario"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="nombre"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nombre *"
                  size="small"
                  fullWidth
                  placeholder="Dr. Juan García"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="matricula"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Matrícula *"
                  size="small"
                  fullWidth
                  placeholder="MV-1234"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!isDirty || mutation.isPending || hasFormErrors}
              onClick={() => void handleSubmit(onSubmit)()}
              startIcon={
                mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {mutation.isPending ? 'Guardando...' : 'Guardar firmante'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
